import React, { useEffect, useState } from "react";
import { X, Check, Pencil, Download, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

/**
 * Bottom sheet shown when the Home FAB is tapped. Renders the user's weekly
 * grocery list (already in the report payload as `weeklyList`) with three
 * primary CTAs in a sticky footer: Confirm / Edit / Download.
 *
 * Mobile: slide-up sheet from bottom with drag handle.
 * Desktop: centred modal.
 */

const formatINR = (n) =>
  `₹${new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(n || 0)))}`;

export default function WeeklyListSheet({ open, onClose, weeklyList, onConfirm, onEdit, onDownload }) {
  const [mounted, setMounted] = useState(false);
  // Trigger enter animation on next paint
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setMounted(true));
    } else {
      setMounted(false);
    }
  }, [open]);

  if (!open) return null;

  const items = Array.isArray(weeklyList?.items) ? weeklyList.items : [];
  const grouped = items.reduce((acc, it) => {
    const cat = it.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(it);
    return acc;
  }, {});
  const categories = Object.keys(grouped);
  const totalEstimate = items.reduce((s, it) => s + (Number(it.estimatedPrice) || 0), 0);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end lg:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="weekly-list-sheet"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="weekly-list-title"
      style={{
        animation: "wlFade 0.28s ease-out forwards",
      }}
    >
      <style>{`
        @keyframes wlFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wlSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes wlSlideUpDesk { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full lg:w-[520px] max-h-[88vh] bg-[#0F0F14] border-t border-x border-[#2A2A35] lg:border-[#2A2A35] flex flex-col"
        style={{
          animation: mounted
            ? `wlSlideUp 0.32s cubic-bezier(0.2,0.7,0.2,1) forwards, wlSlideUpDesk 0.32s ease-out forwards`
            : "none",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Drag handle (mobile only) */}
        <div className="lg:hidden flex justify-center py-2">
          <div className="w-10 h-1 bg-[#2A2A35] rounded-full" aria-hidden />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-3 pb-4 border-b border-[#2A2A35]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1 flex items-center gap-2">
              <ShoppingBag className="w-3 h-3" /> This week's list
            </div>
            <h2
              id="weekly-list-title"
              className="font-display uppercase text-2xl"
              style={{ fontFamily: "'Pirata One', serif" }}
              data-testid="weekly-list-title"
            >
              {weeklyList?.title || "Your Saturday cart"}
            </h2>
            <p className="text-xs text-[#888] mt-1">
              {items.length} {items.length === 1 ? "item" : "items"}
              {totalEstimate > 0 && (
                <>
                  <span className="text-[#555]"> · </span>
                  Est. <span className="font-mono-num">{formatINR(totalEstimate)}</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 -mr-2 -mt-1 text-[#888] hover:text-[#F0EDE8]"
            data-testid="weekly-list-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 && (
            <div className="text-center py-12">
              <ShoppingBag className="w-8 h-8 mx-auto text-[#555] mb-3" />
              <p className="text-sm text-[#888]">
                Upload at least 3 invoices to see your AI-curated weekly list.
              </p>
            </div>
          )}
          {categories.map((cat) => (
            <div key={cat} className="mb-5 last:mb-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold mb-2">
                {cat}
                <span className="text-[#555] ml-2">· {grouped[cat].length}</span>
              </div>
              <ul className="space-y-1.5">
                {grouped[cat].map((it, i) => (
                  <li
                    key={`${cat}-${i}`}
                    className="flex items-center justify-between gap-3 py-2 border-b border-[#2A2A35]/40 last:border-0"
                    data-testid={`weekly-list-item-${i}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <span
                        className="w-1 h-1 rounded-full bg-[#FF6B00] shrink-0"
                        aria-hidden
                      />
                      <span className="truncate text-sm text-[#F0EDE8]">{it.item || it.name}</span>
                      {it.quantity && (
                        <span className="text-[10px] text-[#555] uppercase tracking-wider shrink-0">
                          {it.quantity}
                        </span>
                      )}
                    </div>
                    {it.estimatedPrice && (
                      <span className="font-mono-num text-xs text-[#888] shrink-0">
                        {formatINR(it.estimatedPrice)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Sticky footer CTAs */}
        <div className="border-t border-[#2A2A35] bg-[#0A0A0F] px-3 py-3 grid grid-cols-3 gap-2">
          <SheetCta
            onClick={() => {
              onConfirm?.();
              toast.success("List confirmed — we'll send it to your WhatsApp on Saturday.");
              onClose();
            }}
            primary
            icon={Check}
            label="Confirm"
            testid="weekly-list-confirm"
            disabled={items.length === 0}
          />
          <SheetCta
            onClick={() => {
              onEdit?.();
              onClose();
            }}
            icon={Pencil}
            label="Edit"
            testid="weekly-list-edit"
            disabled={items.length === 0}
          />
          <SheetCta
            onClick={() => {
              onDownload?.();
              onClose();
            }}
            icon={Download}
            label="Download"
            testid="weekly-list-download"
            disabled={items.length === 0}
          />
        </div>
      </div>
    </div>
  );
}

const SheetCta = ({ onClick, primary, icon: Icon, label, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`flex flex-col items-center justify-center gap-1 py-3 text-[10px] uppercase tracking-[0.18em] font-bold transition-colors ${
      disabled
        ? "bg-[#14141A] text-[#444] cursor-not-allowed"
        : primary
        ? "bg-[#FF6B00] text-white hover:bg-[#FF8500]"
        : "bg-[#14141A] text-[#F0EDE8] border border-[#2A2A35] hover:border-[#FF6B00] hover:text-[#FF6B00]"
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);
